import { ref, computed, Ref } from 'vue';
import type { InjectionKey } from 'vue';
import {
  StepType,
  type ClientConfig,
  type ContactConfig,
  type GlobalConfig,
  type Policy,
  type PublicWidgetOptions,
  type StepKey,
  type StepsConfig,
  type Theme,
  type WidgetConfig,
} from '../types/widget';
import { request } from './request';
import { useSubmission } from '../components/form/util/composition';
import { RecursivePartial } from 'src/types/helpers';
import { isSource, Source } from 'src/types/api';

const rootKey = Symbol('root_element') as InjectionKey<string>;
const configChangeHistory = ref<string[]>([]);
const defaultConfig: GlobalConfig = {
  clientId: '',
  apiKey: '',
  theme: {
    primary: '#2A609D',
    secondary: '#004FBC',
    positive: '#408825',
    negative: '#DE0808',
    fontPrimary: '#000000',
    fontSecondary: '#dddddd',
    borderRadius: '0.3rem',
    shadow: 'none',
    background: '#FFFFFF',
    backgroundSoft: '#F3F4F6',
    primaryRegularUrl: '',
    primaryBoldUrl: '',
    secondaryRegularUrl: '',
    secondaryBoldUrl: '',
  } satisfies Theme,
  steps: {
    title: '',
    description: '',
    components: [
      {
        name: '',
        description: '',
        icon: '',
        key: '' as StepKey,
        form: {
          schema: {
            type: 'object',
            properties: {},
          },
          uischema: {
            type: 'VerticalLayout',
            elements: [],
          },
          options: {
            submit: false,
          },
        },
      },
    ],
  } satisfies StepsConfig,
  contact: {
    phone: '',
    email: '',
  } satisfies ContactConfig,
  isConfigLoading: true,
  error: '',
  name: '',
  allowedDomains: [] as string[],
  correspondenceEmail: [] as string[],
  rootKey,
  id: '',
  sessionTracking: false,
  onSuccess: undefined,
  onError: undefined,
  initialStepKey: '' as StepKey | undefined,
  selectedClaim: '' as StepKey | undefined,
  clientTheme: 'default' as ClientConfig,
  iconTheme: 'default' as ClientConfig,
  sessionData: {
    createdAt: '',
    userId: '',
    id: '',
    currentSession: {},
    submission: {},
    statistics: { preSubmissionAccessCount: 0, postSubmissionAccessCount: 0 },
    userStatistics: undefined,
  },
  context: {},
  analytics: {
    enabled: true,
    gaId: '',
    clarityId: '',
  },
};

const originalConfig = ref('');
export const config: Ref<GlobalConfig> = ref({} as GlobalConfig);

function mergePublicConfig(options: RecursivePartial<WidgetConfig>) {
  if (options.contact) {
    config.value.contact = { ...config.value.contact, ...options.contact };
  }
  if (options.policy) {
    config.value.policy = options.policy as Policy | Policy[];
    if (Array.isArray(config.value.policy) && config.value.policy?.length === 1) {
      setWidgetInsuranceDetails(config.value.policy[0]);
    } else if (!Array.isArray(config.value.policy)) {
      setWidgetInsuranceDetails(config.value.policy);
    }
  } else {
    config.value.insurance = undefined;
    if (options.driver) {
      config.value.driver = options.driver;
    }
    if (options.vehicle) {
      config.value.vehicle = options.vehicle;
    }
  }

  if (options.theme) {
    config.value.theme = { ...config.value.theme, ...options.theme };
  }
  if (options.state && isSource(options.state)) {
    config.value.state = { ...config.value.state, ...options.state } as RecursivePartial<Source> &
      Pick<Source, 'stepKey'>;
  }
  if (options.initialStepKey) {
    config.value.initialStepKey = options.initialStepKey;
  }
  if (options.fontFamily) {
    config.value.fontFamily = options.fontFamily;
  }
  if (options.privacyPolicyUrl) {
    config.value.privacyPolicyUrl = options.privacyPolicyUrl;
  }
  if (options.reference) {
    config.value.reference = options.reference;
  }
  if (options.analytics) {
    config.value.analytics = { ...config.value.analytics, ...options.analytics };
  }
  listeners.forEach((listener) => listener.off());
  const { onError, onSuccess, onChange } = useSubmission();
  if (config.value.onSuccess) {
    listeners[0] = onSuccess(config.value.onSuccess);
  }
  if (config.value.onError) {
    listeners[1] = onError(config.value.onError);
  }
  if (config.value.onChange) {
    listeners[2] = onChange(config.value.onChange);
  }
}

export function getComponentStyle() {
  return config.value.theme?.components;
}

const listeners: Array<{ off: () => void }> = [];
export function applyConfig(globalConfig: Partial<GlobalConfig>, publicConfig?: RecursivePartial<PublicWidgetOptions>) {
  // do not allow overwrite of the apiKey
  const apiKey = config.value.apiKey;
  Object.assign(config.value, globalConfig);
  config.value.apiKey = apiKey;
  if (publicConfig) {
    mergePublicConfig(publicConfig);
  }
  if (publicConfig?.currentUserId) {
    config.value.currentUserId = publicConfig.currentUserId;
    config.value.sessionTracking = publicConfig.sessionTracking;
  }
  const policy = config.value.policy as Policy | Policy[];
  if (policy) {
    if (Array.isArray(policy) && policy?.length === 1) {
      setWidgetInsuranceDetails(policy[0]);
    } else if (!Array.isArray(policy)) {
      setWidgetInsuranceDetails(policy);
    }
  }
  originalConfig.value = JSON.stringify(config.value);
  listeners.forEach((listener) => listener.off());
  const { onError, onSuccess, onChange } = useSubmission();
  if (config.value.onSuccess) {
    listeners[0] = onSuccess(config.value.onSuccess);
  }
  if (config.value.onError) {
    listeners[1] = onError(config.value.onError);
  }
  if (config.value.onChange) {
    listeners[2] = onChange(config.value.onChange);
  }
}

/**
 * Use applyConfig to apply any changes at runtime if you have not had the chance to change them at initialization.
 * @param options - Configuration options for the widget.
 * @example
 *
 * ```ts
 * interface WidgetConfig {
 *   user: {
 *     id?: string;
 *     firstName?: string;
 *     lastName?: string;
 *     email?: string
 *     ...
 *   }
 *   ... // all other optional parameter
 * }
 * ```
 *
 * @returns A Promise that resolves when the changes are applied and the widget is ready to use whit updated config.
 */
export async function applyPublicConfig(options: RecursivePartial<WidgetConfig>): Promise<void>;
/**
 * Use applyConfig to apply any changes at runtime if you have not had the chance to change them at initialization.
 * @param token - JSON web token (JWT) encoding the configuration settings (options: RecursivePartial<WidgetConfig>) - any token you provide will be decoded using your API key.
 * @example
 *
 * ```ts
 * interface WidgetConfig {
 *   user: {
 *     id?: string;
 *     firstName?: string;
 *     lastName?: string;
 *     email?: string
 *     ...
 *   }
 *   ... // all other optional parameter
 * }
 * ```
 *
 * @returns A Promise that resolves when the changes are applied and the widget is ready to use whit updated config.
 */
export async function applyPublicConfig(token: string): Promise<void>;
export async function applyPublicConfig(options: RecursivePartial<WidgetConfig> | string) {
  if (configChangeHistory.value.includes(JSON.stringify(options))) {
    return;
  }
  configChangeHistory.value.push(JSON.stringify(options));
  if (typeof options === 'string') {
    const [result, error] = await request.validate({ token: options });
    if (error?.message) {
      throw error.message;
    }
    if (!result) {
      throw 'Unknown error happend, please try again later';
    }
    mergePublicConfig(result);
    return;
  }
  mergePublicConfig(options);
}

export function resetConfig() {
  const original = JSON.parse(originalConfig.value);
  original.context = config.value.context;
  original.sessionData = config.value.sessionData;
  original.isConfigLoading = false;
  applyConfig(original);
  configChangeHistory.value = [];
}

applyConfig(defaultConfig);

export const themeIcons = computed(() => {
  const icons = {
    directions: 'mdi:directions',
    certified: 'general/Certified.vue',
    noResult: 'general/NoResult.vue',
    arrowRight: 'mdi:arrow-right',
    calendar: 'material-symbols:calendar-today',
    car: 'mdi:car-outline',
    check: 'mdi:check',
    checkCircle: 'mdi:arrow-right',
    chevronDown: 'mdi:chevron-down',
    chevronLeft: 'mdi:chevron-left',
    chevronRight: 'mdi:chevron-right',
    chevronUp: 'mdi:chevron-up',
    circle: 'mdi:circle-outline',
    clipBoard: 'fluent:clipboard-20-regular',
    clipBoardCheck: 'fluent:clipboard-checkmark-20-regular',
    close: 'mdi:close',
    closeCircle: 'mdi:close-circle',
    cyclist: 'mdi:bicycle',
    damage: 'mdi:shield-car',
    delete: 'mdi:trash-can-outline',
    driver: 'mdi:car-outline',
    edit: 'material-symbols:edit',
    fire: 'mdi:fire',
    flood: 'mdi:water',
    hail: 'mdi:weather-hail',
    idCard: 'mdi:card-account-details-outline',
    info: 'mdi:information-outline',
    locked: 'mdi:lock',
    mail: 'mdi:email-outline',
    marker: 'mage:map-marker',
    motorcyclist: 'ph:motorcycle-fill',
    multipleCars: 'mdi:car-multiple',
    other: 'mdi:account-outline',
    otherDamage: 'mdi:shield-car',
    otherWeather: 'mdi:weather-sunny',
    pedestrian: 'mdi:walk',
    person: 'mdi:account-outline',
    phone: 'solar:phone-outline',
    plus: 'mdi:plus',
    save: 'ic:baseline-check',
    search: 'mdi:magnify',
    theft: 'mdi:shield-car',
    theftCar: 'mdi:shield-car',
    theftFromCar: 'mdi:shield-car',
    trash: 'ic:outline-delete',
    weather: 'mdi:weather-sunny',
    windscreen: 'mdi:car-windshield',
    witness: 'mdi:message-check-outline',
    image: 'mdi:image-outline',
    wrench: 'mdi:wrench',
    agentPhone: 'general/Call.vue',
    rsa: 'general/RSA.vue',
    sos: 'general/SOSButton.vue',
    bcall: 'general/BCall.vue',
    ecall: 'general/ECall.vue',
    acall: 'general/ACall.vue',
    audiLogo: 'general/AudiLogo.vue',
    seatLogo: 'general/SeatLogo.vue',
    suzukiLogo: 'general/SuzukiLogo.vue',
    teslaLogo: 'general/TeslaLogo.vue',
    skodaLogo: 'general/SkodaLogo.vue',
    jaguarLogo: 'general/JaguarLogo.vue',
    landroverLogo: 'general/LandroverLogo.vue',
    cupraLogo: 'general/CupraLogo.vue',
    volkswagenLogo: 'general/VolkswagenLogo.vue',
    bentleyLogo: 'general/BentleyLogo.vue',
    mercedesLogo: 'general/MercedesLogo.vue',
    repairFinder: 'general/RepairFinder.vue',
  };

  if (checkIconTheme('samsung')) {
    icons.car = 'samsung/Car.vue';
    icons.multipleCars = 'samsung/Cars.vue';
    icons.phone = 'samsung/Phone.vue';
    icons.mail = 'samsung/Mail.vue';
  }
  if (checkIconTheme('volvo')) {
    icons.bcall = 'general/BCall.vue';
    icons.rsa = 'general/RSA.vue';
    icons.arrowRight = '';
    icons.calendar = 'volvo-calendar-24';
    icons.car = 'volvo-car-car-40';
    icons.checkCircle = 'mdi:arrow-right';
    icons.chevronDown = 'volvo-navigation-chevrondown-16';
    icons.chevronLeft = 'volvo-navigation-chevronback-16';
    icons.chevronRight = 'volvo-navigation-chevronforward-12';
    icons.chevronUp = 'volvo-navigation-chevronup-16';
    icons.circle = 'mdi:arrow-right';
    icons.close = 'volvo-navigation-close-16';
    icons.closeCircle = 'volvo-mediacircled-close-24';
    icons.cyclist = '';
    icons.damage = 'volvo-car-carside-40';
    icons.delete = 'volvo-delete-24';
    icons.driver = '';
    icons.edit = 'volvo-edit-24';
    icons.fire = 'volvo-weather-sun-40';
    icons.flood = 'volvo-car-cardroplets-40';
    icons.hail = 'volvo-weather-rain-40';
    icons.idCard = 'volvo-other-creditcard-24';
    icons.info = 'volvo-info-24';
    icons.locked = 'volvo-locked-40';
    icons.mail = 'volvo-communication-email-24';
    icons.marker = 'volvo-map-mappin-24';
    icons.motorcyclist = '';
    icons.multipleCars = 'volvo-car-blis-40';
    icons.other = '';
    icons.otherDamage = 'volvo-car-carshieldtick-40';
    icons.otherWeather = 'volvo-weather-cloud-40';
    icons.pedestrian = '';
    icons.person = 'volvo-avatar-24';
    icons.phone = 'volvo-communication-phone-24';
    icons.plus = 'volvo-navigation-plus-24';
    icons.save = 'volvo-checkmark-24';
    icons.theft = 'volvo-car-key-40';
    icons.theftCar = 'volvo-car-car-40';
    icons.theftFromCar = 'volvo-avatar-40';
    icons.trash = 'volvo-delete-24';
    icons.weather = 'volvo-weather-cloud-40';
    icons.windscreen = 'volvo-car-windscreenwiper-40';
    icons.witness = '';
    icons.wrench = 'ph:wrench-thin';
  }

  if (checkIconTheme('jaguar')) {
    icons.calendar = 'jaguar/Calendar.vue';
    icons.car = 'jaguar/Car.vue';
    icons.checkCircle = 'mdi:minus';
    icons.chevronDown = 'volvo-navigation-chevrondown-16';
    icons.chevronLeft = 'volvo-navigation-chevronback-16';
    icons.chevronRight = 'volvo-navigation-chevronforward-16';
    icons.chevronUp = 'volvo-navigation-chevronup-16';
    icons.circle = 'mdi:minus';
    icons.damage = 'jaguar/Bodyshop.vue';
    icons.delete = 'volvo-delete-24';
    icons.edit = 'volvo-edit-24';
    icons.fire = 'jaguar/Fire.vue';
    icons.flood = 'jaguar/Flood.vue';
    icons.hail = 'jaguar/Hail.vue';
    icons.idCard = 'jaguar/Card.vue';
    icons.info = 'jaguar/Info.vue';
    icons.locked = 'jaguar/Secure.vue';
    icons.marker = 'jaguar/Pin.vue';
    icons.multipleCars = 'jaguar/Cars.vue';
    icons.otherDamage = 'jaguar/CarCheck.vue';
    icons.otherWeather = 'jaguar/Rain.vue';
    icons.person = 'jaguar/User.vue';
    icons.plus = 'jaguar/Plus.vue';
    icons.theft = 'jaguar/Key.vue';
    icons.theftCar = 'jaguar/Car.vue';
    icons.theftFromCar = 'jaguar/Theft.vue';
    icons.trash = 'volvo-delete-24';
    icons.weather = 'jaguar/Rain.vue';
    icons.windscreen = 'jaguar/WindscreenWiper.vue';
  }

  if (checkIconTheme('landrover')) {
    icons.calendar = 'landrover/Calendar.vue';
    icons.car = 'landrover/Car.vue';
    icons.checkCircle = 'mdi:minus';
    icons.chevronDown = 'volvo-navigation-chevrondown-16';
    icons.chevronLeft = 'volvo-navigation-chevronback-16';
    icons.chevronRight = 'volvo-navigation-chevronforward-16';
    icons.chevronUp = 'volvo-navigation-chevronup-16';
    icons.delete = 'volvo-delete-24';
    icons.circle = 'mdi:minus';
    icons.damage = 'landrover/Bodyshop.vue';
    icons.edit = 'volvo-edit-24';
    icons.fire = 'landrover/Fire.vue';
    icons.flood = 'landrover/Flood.vue';
    icons.hail = 'landrover/Hail.vue';
    icons.idCard = 'landrover/Card.vue';
    icons.info = 'landrover/Info.vue';
    icons.locked = 'landrover/Secure.vue';
    icons.multipleCars = 'landrover/Cars.vue';
    icons.otherDamage = 'landrover/CarCheck.vue';
    icons.otherWeather = 'landrover/Rain.vue';
    icons.person = 'landrover/User.vue';
    icons.plus = 'landrover/Plus.vue';
    icons.theft = 'landrover/Key.vue';
    icons.theftCar = 'landrover/Car.vue';
    icons.theftFromCar = 'landrover/Theft.vue';
    icons.trash = 'volvo-delete-24';
    icons.weather = 'landrover/Rain.vue';
    icons.windscreen = 'landrover/WindscreenWiper.vue';
  }

  if (checkIconTheme('jlr', 'referral')) {
    icons.calendar = 'jlr/Calendar.vue';
    icons.checkCircle = 'jlr/Bullet.vue';
    icons.chevronDown = 'mdi:chevron-down';
    icons.chevronLeft = 'mdi:chevron-left';
    icons.chevronRight = 'mdi:chevron-right';
    icons.chevronUp = 'mdi:chevron-up';
    icons.circle = 'mdi:arrow-right';
    icons.edit = 'jlr/Pencil.vue';
    icons.idCard = 'landrover/Card.vue';
    icons.info = 'jlr/Info.vue';
    icons.jaguarLogo = 'general/JaguarLogo.vue';
    icons.landroverLogo = 'general/LandroverLogo.vue';
    icons.person = 'landrover/User.vue';
    icons.plus = 'landrover/Plus.vue';
    icons.rsa = 'general/RSA.vue';
    icons.sos = 'jlr/SOSButton.vue';
    icons.trash = 'jlr/Delete.vue';
  }

  if (checkIconTheme('mini')) {
    icons.arrowRight = '';
    icons.calendar = 'mini/Calendar.vue';
    icons.car = 'mini/Car.vue';
    icons.checkCircle = 'mdi:arrow-right';
    icons.circle = 'mdi:arrow-right';
    icons.cyclist = '';
    icons.damage = 'mini/Bodyshop.vue';
    icons.delete = 'mdi:trash-can-outline';
    icons.driver = '';
    icons.edit = 'mini/Edit.vue';
    icons.fire = 'mini/Fire.vue';
    icons.flood = 'mini/Flood.vue';
    icons.hail = 'mini/Hail.vue';
    icons.idCard = 'mini/Card.vue';
    icons.locked = 'mini/Secure.vue';
    icons.marker = 'mini/Pin.vue';
    icons.motorcyclist = '';
    icons.multipleCars = 'mini/Cars.vue';
    icons.other = '';
    icons.otherDamage = 'mini/Other.vue';
    icons.otherWeather = 'mini/Rain.vue';
    icons.pedestrian = '';
    icons.person = 'mini/User.vue';
    icons.plus = 'mini/Plus.vue';
    icons.theft = 'mini/Theft.vue';
    icons.theftCar = 'mini/Car.vue';
    icons.theftFromCar = 'mini/Theft.vue';
    icons.trash = 'volvo-delete-24';
    icons.weather = 'mini/Rain.vue';
    icons.windscreen = 'mini/WindscreenWiper.vue';
    icons.witness = '';
  }

  if (checkIconTheme('toyota')) {
    icons.car = 'toyota/Car.vue';
    icons.multipleCars = 'toyota/Cars.vue';
    icons.mail = 'toyota/Mail.vue';
    icons.info = 'toyota/Info.vue';
    icons.phone = 'toyota/Phone.vue';
    icons.wrench = 'ph:wrench';
  }

  if (checkIconTheme('bmw')) {
    icons.arrowRight = '';
    icons.calendar = 'tabler:calendar';
    icons.car = 'bmw/Car.vue';
    icons.checkCircle = 'mdi:arrow-right';
    icons.chevronDown = 'bmw/ChevronDown.vue';
    icons.chevronLeft = 'volvo-navigation-chevronback-16';
    icons.chevronRight = 'volvo-navigation-chevronforward-16';
    icons.chevronUp = 'volvo-navigation-chevronup-16';
    icons.circle = 'mdi:circle-outline';
    icons.cyclist = '';
    icons.damage = 'bmw/Bodyshop.vue';
    icons.delete = 'mdi:trash-can-outline';
    icons.driver = '';
    icons.edit = 'bmw/Edit.vue';
    icons.fire = 'bmw/Fire.vue';
    icons.flood = 'bmw/Flood.vue';
    icons.hail = 'bmw/Hail.vue';
    icons.idCard = 'bmw/Card.vue';
    icons.info = 'mdi:information-outline';
    icons.locked = 'bmw/Secure.vue';
    icons.marker = 'bmw/Pin.vue';
    icons.motorcyclist = '';
    icons.multipleCars = 'bmw/Cars.vue';
    icons.other = '';
    icons.otherDamage = 'bmw/Other.vue';
    icons.otherWeather = 'bmw/Rain.vue';
    icons.pedestrian = '';
    icons.person = 'bmw/User.vue';
    icons.plus = 'bmw/Plus.vue';
    icons.search = 'bmw/Search.vue';
    icons.theft = 'bmw/Theft.vue';
    icons.theftCar = 'bmw/Car.vue';
    icons.theftFromCar = 'bmw/Theft.vue';
    icons.trash = 'ph:trash-light';
    icons.weather = 'bmw/Rain.vue';
    icons.windscreen = 'bmw/WindscreenWiper.vue';
    icons.witness = '';
  }
  if (checkIconTheme('mopar')) {
    icons.arrowRight = '';
    icons.calendar = 'mopar/Calendar.vue';
    icons.car = 'mopar/Car.vue';
    icons.checkCircle = 'mdi:arrow-right';
    icons.chevronDown = 'mopar/ChevronDown.vue';
    icons.chevronLeft = 'volvo-navigation-chevronback-16';
    icons.chevronRight = 'volvo-navigation-chevronforward-16';
    icons.chevronUp = 'volvo-navigation-chevronup-16';
    icons.circle = 'mdi:circle-outline';
    icons.cyclist = '';
    icons.damage = 'mopar/Bodyshop.vue';
    icons.delete = 'ant-design:delete-filled';
    icons.driver = '';
    icons.edit = 'ic:baseline-edit';
    icons.fire = 'mopar/Fire.vue';
    icons.flood = 'mopar/Flood.vue';
    icons.hail = 'mopar/Hail.vue';
    icons.idCard = 'mopar/Card.vue';
    icons.info = 'mdi:information';
    icons.locked = 'mopar/Secure.vue';
    icons.marker = 'mdi:map-marker';
    icons.motorcyclist = '';
    icons.multipleCars = 'mopar/Cars.vue';
    icons.other = '';
    icons.otherDamage = 'mopar/Other.vue';
    icons.otherWeather = 'mopar/Rain.vue';
    icons.pedestrian = '';
    icons.person = 'mopar/User.vue';
    icons.phone = 'mdi:phone';
    icons.mail = 'mopar/Mail.vue';
    icons.plus = 'mopar/Plus.vue';
    icons.search = 'mopar/Search.vue';
    icons.theft = 'mopar/Theft.vue';
    icons.theftCar = 'mopar/Car.vue';
    icons.theftFromCar = 'mopar/Theft.vue';
    icons.trash = 'ph:trash-light';
    icons.weather = 'mopar/Rain.vue';
    icons.windscreen = 'mopar/WindscreenWiper.vue';
    icons.witness = '';
  }
  if (checkIconTheme('jeep')) {
    icons.arrowRight = '';
    icons.calendar = 'jeep/Calendar.vue';
    icons.car = 'jeep/Car.vue';
    icons.checkCircle = 'mdi:arrow-right';
    icons.chevronDown = 'jeep/ChevronDown.vue';
    icons.chevronLeft = 'jeep/ChevronLeft.vue';
    icons.chevronRight = 'jeep/ChevronRight.vue';
    icons.chevronUp = 'jeep/ChevronUp.vue';
    icons.circle = 'mdi:circle-outline';
    icons.cyclist = '';
    icons.damage = 'jeep/Bodyshop.vue';
    icons.delete = 'jeep/Trash.vue';
    icons.driver = '';
    icons.edit = 'jeep/Edit.vue';
    icons.fire = 'jeep/Fire.vue';
    icons.flood = 'jeep/Flood.vue';
    icons.hail = 'jeep/Hail.vue';
    icons.idCard = 'jeep/Card.vue';
    icons.info = 'jeep/Info.vue';
    icons.locked = 'jeep/Secure.vue';
    icons.marker = 'jeep/Pin.vue';
    icons.motorcyclist = '';
    icons.multipleCars = 'jeep/Cars.vue';
    icons.other = '';
    icons.otherDamage = 'jeep/Other.vue';
    icons.otherWeather = 'jeep/Rain.vue';
    icons.pedestrian = '';
    icons.person = 'jeep/User.vue';
    icons.phone = 'jeep/Phone.vue';
    icons.mail = 'jeep/Mail.vue';
    icons.plus = 'jeep/Plus.vue';
    icons.search = 'jeep/Search.vue';
    icons.theft = 'jeep/Theft.vue';
    icons.theftCar = 'jeep/Car.vue';
    icons.theftFromCar = 'jeep/Theft.vue';
    icons.trash = 'jeep/Trash.vue';
    icons.weather = 'jeep/Rain.vue';
    icons.windscreen = 'jeep/WindscreenWiper.vue';
    icons.witness = '';
  }
  // TODO: Add icons for Mercedes
  if (checkIconTheme('mercedes')) {
    icons.calendar = 'mercedes/Calendar.vue';
    icons.checkCircle = 'mdi:arrow-right';
    icons.chevronDown = 'mdi:chevron-down';
    icons.chevronLeft = 'volvo-navigation-chevronback-16';
    icons.chevronRight = 'volvo-navigation-chevronforward-16';
    icons.chevronUp = 'mdi:chevron-up';
    icons.circle = 'mdi:arrow-right';
    icons.idCard = 'mercedes/Card.vue';
    icons.marker = 'mercedes/Pin.vue';
    icons.person = 'mercedes/User.vue';
    icons.phone = 'mercedes/Phone.vue';
    icons.plus = 'mercedes/Plus.vue';
    icons.trash = 'volvo-delete-24';
  }

  if (checkIconTheme('volkswagen', 'vwg')) {
    icons.arrowRight = '';
    icons.calendar = 'volkswagen/Calendar.vue';
    icons.car = 'volkswagen/Car.vue';
    icons.checkCircle = 'volkswagen/ArrowRight.vue';
    icons.chevronDown = 'volkswagen/ChevronDown.vue';
    icons.chevronRight = 'volkswagen/ChevronRight.vue';
    icons.delete = 'volkswagen/Delete.vue';
    icons.edit = 'volkswagen/Edit.vue';
    icons.info = 'volkswagen/Info.vue';
    icons.plus = 'volvo-navigation-plus-24';
    icons.wrench = 'volkswagen/Wrench.vue';

    icons.bcall = 'general/BCall.vue';
    icons.ecall = 'general/ECall.vue';
    icons.rsa = 'general/RSA.vue';
    icons.sos = 'general/SOSButton.vue';
    icons.arrowRight = '';
  }
  if (checkIconTheme('bentley')) {
    icons.arrowRight = '';
    icons.calendar = 'bentley/Calendar.vue';
    icons.car = 'bentley/Car.vue';
    icons.multipleCars = 'bentley/Cars.vue';
    icons.checkCircle = 'bentley/ArrowRight.vue';
    icons.chevronDown = 'bentley/ChevronDown.vue';
    icons.chevronRight = 'bentley/ChevronRight.vue';
    icons.delete = 'bentley/Delete.vue';
    icons.edit = 'bentley/Edit.vue';
    icons.info = 'bentley/Info.vue';
    icons.plus = 'volvo-navigation-plus-24';
    icons.wrench = 'bentley/Wrench.vue';
    icons.marker = 'bentley/Pin.vue';
    icons.mail = 'bentley/Mail.vue';
    icons.phone = 'bentley/Phone.vue';

    icons.bcall = 'general/BCall.vue';
    icons.ecall = 'general/ECall.vue';
    icons.rsa = 'general/RSA.vue';
    icons.sos = 'general/SOSButton.vue';
  }

  if (checkIconTheme('aa')) {
    icons.arrowRight = '';
    icons.calendar = 'aa/CalendarToday.vue';
    icons.car = 'aa/Car.vue';
    icons.multipleCars = 'aa/Multiple.vue';
    icons.checkCircle = 'aa/ArrowRight.vue';
    icons.chevronDown = 'aa/ChevronDown.vue';
    icons.chevronUp = 'aa/ChevronUp.vue';
    icons.chevronRight = 'aa/ChevronRight.vue';
    icons.chevronLeft = 'aa/ChevronLeft.vue';
    icons.delete = 'aa/Delete.vue';
    icons.edit = 'aa/Edit.vue';
    icons.info = 'aa/Info.vue';
    icons.plus = 'volvo-navigation-plus-24';
    icons.wrench = 'aa/Wrench.vue';
    icons.marker = 'aa/Location.vue';
    icons.mail = 'aa/Mail.vue';
    icons.phone = 'aa/Call.vue';
    icons.agentPhone = 'aa/Headphones.vue';
    icons.image = 'aa/Image.vue';
    icons.close = 'aa/Close.vue';
    icons.person = 'aa/User.vue';

    icons.bcall = 'general/BCall.vue';
    icons.ecall = 'general/ECall.vue';
    icons.rsa = 'general/RSA.vue';
    icons.sos = 'general/SOSButton.vue';
  }
  if (checkIconTheme('tesla')) {
    icons.arrowRight = '';
    icons.calendar = 'tesla/CalendarToday.vue';
    icons.car = 'tesla/Car.vue';
    icons.multipleCars = 'tesla/Multiple.vue';
    icons.checkCircle = 'tesla/ArrowRight.vue';
    icons.chevronDown = 'tesla/ChevronDown.vue';
    icons.chevronUp = 'tesla/ChevronUp.vue';
    icons.chevronRight = 'tesla/ChevronRight.vue';
    icons.chevronLeft = 'tesla/ChevronLeft.vue';
    icons.delete = 'tesla/Delete.vue';
    icons.edit = 'tesla/Edit.vue';
    icons.info = 'tesla/Info.vue';
    icons.plus = 'volvo-navigation-plus-24';
    icons.wrench = 'tesla/Wrench.vue';
    icons.marker = 'tesla/Location.vue';
    icons.mail = 'tesla/Mail.vue';
    icons.phone = 'tesla/Call.vue';
    icons.agentPhone = 'tesla/Headphones.vue';
    icons.image = 'tesla/Image.vue';
    icons.close = 'tesla/Close.vue';
    icons.person = 'tesla/User.vue';

    icons.bcall = 'general/BCall.vue';
    icons.ecall = 'general/ECall.vue';
    icons.rsa = 'general/RSA.vue';
    icons.sos = 'general/SOSButton.vue';
  }

  if (checkIconTheme('common')) {
    icons.arrowRight = 'common/ArrowRight.vue';
    icons.calendar = 'common/CalendarToday.vue';
    icons.car = 'common/Car.vue';
    icons.multipleCars = 'common/Multiple.vue';
    icons.checkCircle = 'common/ArrowRight.vue';
    icons.chevronDown = 'common/ChevronDown.vue';
    icons.chevronUp = 'common/ChevronUp.vue';
    icons.chevronRight = 'common/ChevronRight.vue';
    icons.chevronLeft = 'common/ChevronLeft.vue';
    icons.delete = 'common/Delete.vue';
    icons.edit = 'common/Edit.vue';
    icons.info = 'common/Info.vue';
    icons.plus = 'volvo-navigation-plus-24';
    icons.wrench = 'common/Wrench.vue';
    icons.marker = 'common/Location.vue';
    icons.mail = 'common/Mail.vue';
    icons.phone = 'common/Call.vue';
    icons.agentPhone = 'common/Headphones.vue';
    icons.image = 'common/Image.vue';
    icons.close = 'common/Close.vue';
    icons.person = 'common/User.vue';

    icons.bcall = 'general/BCall.vue';
    icons.ecall = 'general/ECall.vue';
    icons.rsa = 'general/RSA.vue';
    icons.sos = 'general/SOSButton.vue';
  }

  if (checkIconTheme('suzuki')) {
    icons.arrowRight = 'suzuki/ArrowRight.vue';
    icons.calendar = 'suzuki/CalendarToday.vue';
    icons.car = 'suzuki/Car.vue';
    icons.multipleCars = 'suzuki/Multiple.vue';
    icons.checkCircle = 'suzuki/ArrowRight.vue';
    icons.chevronDown = 'suzuki/ChevronDown.vue';
    icons.chevronUp = 'suzuki/ChevronUp.vue';
    icons.chevronRight = 'suzuki/ChevronRight.vue';
    icons.chevronLeft = 'suzuki/ChevronLeft.vue';
    icons.delete = 'suzuki/Delete.vue';
    icons.edit = 'suzuki/Edit.vue';
    icons.info = 'suzuki/Info.vue';
    icons.plus = 'volvo-navigation-plus-24';
    icons.wrench = 'suzuki/Wrench.vue';
    icons.marker = 'suzuki/Location.vue';
    icons.mail = 'suzuki/Mail.vue';
    icons.phone = 'suzuki/Call.vue';
    icons.agentPhone = 'suzuki/Headphones.vue';
    icons.image = 'suzuki/Image.vue';
    icons.close = 'suzuki/Close.vue';
    icons.person = 'suzuki/User.vue';

    icons.bcall = 'general/BCall.vue';
    icons.ecall = 'general/ECall.vue';
    icons.rsa = 'general/RSA.vue';
    icons.sos = 'general/SOSButton.vue';
  }

  if (checkIconTheme('cinch', 'crashdaddy')) {
    icons.arrowRight = '';
    icons.calendar = 'cinch/CalendarToday.vue';
    icons.car = 'cinch/Car.vue';
    icons.multipleCars = 'cinch/Multiple.vue';
    icons.checkCircle = 'cinch/ArrowRight.vue';
    icons.chevronDown = 'cinch/ChevronDown.vue';
    icons.chevronUp = 'cinch/ChevronUp.vue';
    icons.chevronRight = 'cinch/ChevronRight.vue';
    icons.chevronLeft = 'cinch/ChevronLeft.vue';
    icons.delete = 'cinch/Delete.vue';
    icons.edit = 'cinch/Edit.vue';
    icons.info = 'cinch/Info.vue';
    icons.plus = 'volvo-navigation-plus-24';
    icons.wrench = 'cinch/Wrench.vue';
    icons.marker = 'cinch/Location.vue';
    icons.mail = 'cinch/Mail.vue';
    icons.phone = 'cinch/Call.vue';
    icons.agentPhone = 'cinch/Headphones.vue';
    icons.image = 'cinch/Image.vue';
    icons.close = 'cinch/Close.vue';
    icons.person = 'cinch/User.vue';

    icons.bcall = 'general/BCall.vue';
    icons.ecall = 'general/ECall.vue';
    icons.rsa = 'general/RSA.vue';
    icons.sos = 'general/SOSButton.vue';
  }

  if (checkIconTheme('audi')) {
    icons.arrowRight = '';
    icons.calendar = 'audi/Calendar.vue';
    icons.car = 'audi/Car.vue';
    icons.checkCircle = 'audi/ChevronRight.vue';
    icons.chevronDown = 'audi/ChevronDown.vue';
    icons.chevronRight = 'audi/ChevronRight.vue';
    icons.delete = 'audi/Delete.vue';
    icons.edit = 'audi/Edit.vue';
    icons.info = 'audi/Info.vue';
    icons.phone = 'audi/Phone.vue';
    icons.plus = 'volvo-navigation-plus-24';
    icons.wrench = 'audi/Wrench.vue';
  }
  return icons;
});

export function checkTheme(...themes: Array<ClientConfig | [ClientConfig, StepType]>) {
  const clientTheme = config.value.clientTheme || 'default';
  const clientType = config.value?.settings?.type;
  return themes.some((theme) => {
    if (Array.isArray(theme) && !!clientType) {
      return theme?.[0] === clientTheme && theme?.[1] === clientType;
    }
    if (Array.isArray(theme)) {
      return theme?.[0] === clientTheme;
    }
    return theme === clientTheme;
  });
}

export function checkIconTheme(...themes: Array<ClientConfig | [ClientConfig, StepType]>) {
  const iconTheme = config.value.iconTheme || 'default';
  const clientType = config.value?.settings?.type;
  return themes.some((theme) => {
    if (Array.isArray(theme) && !!clientType) {
      return theme?.[0] === iconTheme && theme?.[1] === clientType;
    }
    if (Array.isArray(theme)) {
      return theme?.[0] === iconTheme;
    }
    return theme === iconTheme;
  });
}

export function getClientTheme() {
  if (!config.value.clientTheme || typeof config.value.clientTheme !== 'string') return undefined;
  return config.value.clientTheme.split('-')[0] as ClientConfig;
}

export function setWidgetInsuranceDetails(insurance: Policy) {
  config.value.insurance = {
    provider: insurance.provider,
    number: insurance.number,
    validFrom: insurance.validFrom,
    validTo: insurance.validTo,
    linkedContacts: insurance.linkedContacts,
  };
  config.value.vehicle = insurance.vehicle;
  config.value.driver = insurance.holder;
}

export function getWidgetType() {
  return config.value.settings?.type;
}
